"use client";

import { useState, useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/utils/cn";

export interface TutorialTooltipProps {
	/** Unique identifier for this tutorial - used for localStorage tracking */
	tutorialId: string;
	/** The content to display in the tutorial */
	children: ReactNode;
	/** Position of the tooltip relative to the target */
	position?: "top" | "bottom" | "left" | "right" | "auto";
	/** Whether to show the tutorial (controlled mode) */
	isOpen?: boolean;
	/** Callback when tutorial is dismissed */
	onDismiss?: () => void;
	/** Additional CSS classes */
	className?: string;
	/** Offset from the target element in pixels */
	offset?: number;
}

const TUTORIAL_DISMISSED_PREFIX = "tutorial_dismissed_";

export function TutorialTooltip({
	tutorialId,
	children,
	position = "auto",
	isOpen: controlledIsOpen,
	onDismiss,
	className,
}: TutorialTooltipProps) {
	const [internalIsOpen, setInternalIsOpen] = useState(false);
	const [calculatedPosition, setCalculatedPosition] = useState<"top" | "bottom" | "left" | "right">("top");
	const tooltipRef = useRef<HTMLDivElement>(null);

	// Determine if tooltip should be open
	const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

	// Check localStorage on mount
	useEffect(() => {
		if (typeof window === "undefined") return;

		const isDismissed = localStorage.getItem(`${TUTORIAL_DISMISSED_PREFIX}${tutorialId}`) === "true";
		if (!isDismissed && controlledIsOpen === undefined) {
			setInternalIsOpen(true);
		}
	}, [tutorialId, controlledIsOpen]);

	// Calculate best position based on available space
	useEffect(() => {
		if (!isOpen || position !== "auto" || !tooltipRef.current) return;

		const tooltip = tooltipRef.current;
		const parent = tooltip.parentElement;
		if (!parent) return;

		const parentRect = parent.getBoundingClientRect();
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		// Calculate available space in each direction
		const spaceTop = parentRect.top;
		const spaceBottom = viewportHeight - parentRect.bottom;
		const spaceLeft = parentRect.left;
		const spaceRight = viewportWidth - parentRect.right;

		// Choose position with most space
		const spaces = {
			top: spaceTop,
			bottom: spaceBottom,
			left: spaceLeft,
			right: spaceRight,
		};

		const bestPosition = (Object.keys(spaces) as Array<keyof typeof spaces>).reduce((a, b) =>
			spaces[a] > spaces[b] ? a : b,
		);

		setCalculatedPosition(bestPosition);
	}, [isOpen, position]);

	const handleDismiss = () => {
		// Save to localStorage
		if (typeof window !== "undefined") {
			localStorage.setItem(`${TUTORIAL_DISMISSED_PREFIX}${tutorialId}`, "true");
		}

		// Update internal state
		setInternalIsOpen(false);

		// Call callback
		onDismiss?.();
	};

	if (!isOpen) return null;

	const finalPosition = position === "auto" ? calculatedPosition : position;

	const positionClasses = {
		top: "bottom-full left-1/2 -translate-x-1/2 mb-3",
		bottom: "top-full left-1/2 -translate-x-1/2 mt-3",
		left: "right-full top-1/2 -translate-y-1/2 mr-3",
		right: "left-full top-1/2 -translate-y-1/2 ml-3",
	};

	const arrowClasses = {
		top: "top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-primary",
		bottom:
			"bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-primary",
		left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-primary",
		right:
			"right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-primary",
	};

	return (
		<div
			ref={tooltipRef}
			className={cn(
				"absolute z-[9999] bg-primary text-white rounded-lg shadow-xl p-4 w-64 max-w-[calc(100vw-2rem)] animate-in fade-in-0 zoom-in-95",
				positionClasses[finalPosition],
				className,
			)}
			role="dialog"
			aria-label="Tutorial"
			style={{
				// Ensure tooltip doesn't go off-screen
				maxWidth: "calc(100vw - 2rem)",
			}}
		>
			{/* Close button */}
			<button
				onClick={handleDismiss}
				className="absolute top-2 right-2 text-white hover:bg-white/20 rounded-full p-1 transition-colors"
				aria-label="Dismiss tutorial"
			>
				<X className="w-4 h-4" />
			</button>

			{/* Content */}
			<div className="pr-6">{children}</div>

			{/* Arrow */}
			<div className={cn("absolute w-0 h-0 border-8", arrowClasses[finalPosition])} aria-hidden="true" />
		</div>
	);
}

/**
 * Hook to check if a tutorial has been dismissed
 */
export function useTutorialDismissed(tutorialId: string): boolean {
	const [isDismissed, setIsDismissed] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const dismissed = localStorage.getItem(`${TUTORIAL_DISMISSED_PREFIX}${tutorialId}`) === "true";
		setIsDismissed(dismissed);
	}, [tutorialId]);

	return isDismissed;
}

/**
 * Reset a tutorial (for development/testing)
 */
export function resetTutorial(tutorialId: string) {
	if (typeof window === "undefined") return;
	localStorage.removeItem(`${TUTORIAL_DISMISSED_PREFIX}${tutorialId}`);
}
